<script setup lang="ts">
import { RouterLink } from 'hybridly/vue';

import AngleRight from '@/icons/line/angle-right.svg';
import ArrowUpRight from '@/icons/line/arrow-up-right.svg';
import Badge from '@/components/badge/Badge.vue';
import { BadgeColor, BadgeSize, BadgeVariant } from '@/hooks/useBadgeClasses';

type RouterLinkOptions = ExtractPropTypes<typeof RouterLink>['options'];

type CardListItemProps = {
  href?: string | URL;
  hrefOptions?: RouterLinkOptions;
  icon?: Component;
  isExternalLink?: boolean;
  isButton?: boolean;
  label?: string;
  labelClasses?: string;
  value?: string;
  badgeLabel?: string;
  badgeIconLeft?: Component;
  badgeColor?: BadgeColor;
  iconClasses?: string;
};

const props = withDefaults(defineProps<CardListItemProps>(), {
  badgeColor: BadgeColor.slate,
  isButton: false,
  labelClasses: 'text-slate-500'
});

defineEmits<{
  click: [];
}>();

const rawIcon = toRaw(props.icon);

const computedLinkComponent = computed(() => {
  if (props.isExternalLink && props.href) {
    return 'a';
  }
  if (props.href) {
    return RouterLink;
  }
  return props.isButton ? 'button' : 'div';
});
</script>

<template>
  <li>
    <component
      :is="computedLinkComponent"
      :href="href"
      :options="hrefOptions"
      class=": flex w-full shrink-0 items-center gap-x-2.5 px-4 py-3.5 text-left text-md leading-5"
      :class="{
        'ring-inset hover:bg-slate-50 focus:outline-none focus:ring-3 focus:ring-primary-300 active:bg-slate-100':
          href || props.isButton
      }"
      @click="$emit('click')"
    >
      <div
        v-if="label || icon"
        class="flex shrink-0 items-center gap-x-2 font-medium"
        :class="labelClasses"
      >
        <component v-if="icon" :is="rawIcon" :class="iconClasses" class="h-4 w-4" />
        <template v-if="label">{{ label }}</template>
      </div>
      <div class="flex grow items-center justify-start gap-x-2 text-slate-900">
        <slot>
          <span v-if="value" class="shrink grow-0 truncate">{{ value }}</span>
          <div v-if="badgeLabel" class="shrink grow-0">
            <Badge
              :color="badgeColor"
              :size="BadgeSize.sm"
              :variant="BadgeVariant.soft"
              :iconLeft="badgeIconLeft"
            >
              {{ badgeLabel }}
            </Badge>
          </div>
        </slot>
      </div>
      <ArrowUpRight v-if="isExternalLink && href" class="h-6 w-6 shrink-0 text-slate-400" />
      <AngleRight v-else-if="href" class="h-6 w-6 shrink-0 text-slate-400" />
    </component>
  </li>
</template>
